import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Search, SlidersHorizontal, X } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { api } from "../lib/api";
import MotorcycleCard from "../components/MotorcycleCard";

const empty = { q: "", merk: "", bouwjaar: "", prijs_max: "", km_max: "", status: "" };

export default function Motoren() {
  useSEO({
    title: "Tweedehands motoren te koop Torhout — gebruikte motor kopen | TT Motorsports",
    description: "Bekijk ons aanbod tweedehands motoren te koop in Torhout. Gekeurde gebruikte motoren van alle merken, gecontroleerd in eigen werkplaats. Filter op merk, bouwjaar, prijs en kilometerstand.",
    keywords: "tweedehands motoren, motoren te koop, gebruikte motor kopen, occasie motor, tweedehands motoren regio, TT Motorsports Torhout",
  });

  const [motos, setMotos] = useState([]);
  const [options, setOptions] = useState({ merken: [], bouwjaren: [] });
  const [filters, setFilters] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api.get("/motorcycles/filters/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
    api.get("/motorcycles", { params }).then((r) => setMotos(r.data)).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const activeCount = Object.values(filters).filter((v) => v !== "").length;

  return (
    <div data-testid="motoren-page">
      <section className="pt-36 pb-12 bg-[#0A0A0B] border-b border-[#2A2B31] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full racing-stripe opacity-[0.04]" />
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 relative">
          <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">Occasies</span>
          <h1 className="heading-xl text-5xl md:text-7xl text-white mt-3">Tweedehands motoren</h1>
          <p className="text-[#9A9CA3] text-lg mt-4 max-w-2xl">Elke occasie wordt in eigen werkplaats gecontroleerd en klaargemaakt voor de baan.</p>
        </div>
      </section>

      <section className="section-pad bg-[#0A0A0B]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Filters */}
          <div>
            <button data-testid="toggle-filters" onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden w-full flex items-center justify-between border border-[#2A2B31] px-4 py-3 mb-4 font-display uppercase font-700 text-white">
              <span className="flex items-center gap-2"><SlidersHorizontal size={18} /> Filters {activeCount > 0 && `(${activeCount})`}</span>
            </button>
            <div className={`${showFilters ? "block" : "hidden"} lg:block bg-[#141518] border border-[#2A2B31] p-6 lg:sticky lg:top-24`}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display uppercase font-700 text-white text-lg">Filters</h3>
                {activeCount > 0 && (
                  <button data-testid="clear-filters" onClick={() => setFilters(empty)} className="text-xs text-[#E10600] flex items-center gap-1 font-mono-tt">
                    <X size={13} /> Wissen
                  </button>
                )}
              </div>

              <label className="block mb-4">
                <span className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3]">Zoeken</span>
                <div className="relative mt-1.5">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A5C63]" />
                  <input data-testid="filter-search" value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Merk of model..."
                    className="w-full bg-[#0A0A0B] border border-[#2A2B31] pl-9 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#E10600]" />
                </div>
              </label>

              <label className="block mb-4">
                <span className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3]">Merk</span>
                <select data-testid="filter-merk" value={filters.merk} onChange={(e) => set("merk", e.target.value)}
                  className="w-full mt-1.5 bg-[#0A0A0B] border border-[#2A2B31] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#E10600]">
                  <option value="">Alle merken</option>
                  {options.merken.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>

              <label className="block mb-4">
                <span className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3]">Bouwjaar</span>
                <select data-testid="filter-bouwjaar" value={filters.bouwjaar} onChange={(e) => set("bouwjaar", e.target.value)}
                  className="w-full mt-1.5 bg-[#0A0A0B] border border-[#2A2B31] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#E10600]">
                  <option value="">Alle jaren</option>
                  {options.bouwjaren.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>

              <label className="block mb-4">
                <span className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3]">Max. prijs (€)</span>
                <input data-testid="filter-prijs" type="number" value={filters.prijs_max} onChange={(e) => set("prijs_max", e.target.value)} placeholder="bv. 8000"
                  className="w-full mt-1.5 bg-[#0A0A0B] border border-[#2A2B31] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#E10600]" />
              </label>

              <label className="block mb-4">
                <span className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3]">Max. kilometerstand</span>
                <input data-testid="filter-km" type="number" value={filters.km_max} onChange={(e) => set("km_max", e.target.value)} placeholder="bv. 200"
                  className="w-full mt-1.5 bg-[#0A0A0B] border border-[#2A2B31] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#E10600]" />
              </label>

              <label className="block">
                <span className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3]">Status</span>
                <select data-testid="filter-status" value={filters.status} onChange={(e) => set("status", e.target.value)}
                  className="w-full mt-1.5 bg-[#0A0A0B] border border-[#2A2B31] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#E10600]">
                  <option value="">Alle</option>
                  <option value="beschikbaar">Beschikbaar</option>
                  <option value="verkocht">Verkocht</option>
                </select>
              </label>
            </div>
          </div>

          {/* Results */}
          <div>
            <p className="font-mono-tt text-sm text-[#9A9CA3] mb-6" data-testid="results-count">
              {loading ? "Laden..." : `${motos.length} motor${motos.length === 1 ? "" : "en"} gevonden`}
            </p>
            {!loading && motos.length === 0 ? (
              <div className="border border-[#2A2B31] p-16 text-center">
                <p className="font-display uppercase text-2xl text-white mb-2">Geen resultaten</p>
                <p className="text-[#9A9CA3]">Pas je filters aan of neem contact met ons op voor je zoekopdracht.</p>
              </div>
            ) : (
              <motion.div layout className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {motos.map((m) => <MotorcycleCard key={m.id} moto={m} />)}
              </motion.div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
